import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, MapPin } from "lucide-react";
import { api } from "../lib/api";
import useSEO from "../hooks/useSEO";
import { FadeUp, SectionTag } from "../components/motion";

export default function Projects() {
  useSEO(
    "Projects & Case Studies — CCTV, Surveillance, Networking & Automation | SGSNet Patna",
    "Case studies from SATI Global Systems (SGSNet): citywide CCTV surveillance grids, data centre modernization, campus fiber networking and business process automation projects delivered from Patna."
  );
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/projects")
      .then((res) => setProjects(res.data))
      .catch(() => setProjects([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <main data-testid="projects-page" className="pt-16 sm:pt-24 pb-24">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <FadeUp><SectionTag>Projects & Case Studies</SectionTag></FadeUp>
        <FadeUp delay={0.1}>
          <h1 className="mt-5 font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.08] max-w-3xl">
            Proof, not <span className="text-blue-600">promises.</span>
          </h1>
        </FadeUp>
        <FadeUp delay={0.2}>
          <p className="mt-6 max-w-2xl text-base sm:text-lg text-slate-600 leading-relaxed">
            Representative engagements across surveillance, infrastructure, networking and
            automation — structured as measurable outcomes.
          </p>
        </FadeUp>

        {loading ? (
          <div className="mt-16 grid sm:grid-cols-2 gap-5">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-72 rounded-2xl bg-panel border border-slate-200 animate-pulse" data-testid="project-skeleton" />
            ))}
          </div>
        ) : (
          <div className="mt-16 grid sm:grid-cols-2 gap-5">
            {projects.map((p, i) => (
              <FadeUp key={p.id} delay={(i % 2) * 0.1}>
                <Link to={`/projects/${p.slug}`} data-testid={`project-card-${p.slug}`}
                  className="group block rounded-2xl overflow-hidden border border-slate-200 bg-panel hover:border-blue-600/40 transition-colors h-full">
                  <div className="relative h-52 overflow-hidden">
                    <img src={p.image} alt={p.name}
                      className="w-full h-full object-cover spotlight-img transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-panel via-transparent to-transparent" />
                    <span className="absolute top-4 left-4 px-2.5 py-1 rounded-full text-[10px] font-mono uppercase tracking-[0.15em] bg-[#FFFFFF]/80 border border-slate-300 text-blue-700">
                      {p.client_sector}
                    </span>
                  </div>
                  <div className="p-6">
                    <h2 className="font-heading text-xl font-semibold text-slate-900 group-hover:text-blue-700 transition-colors">{p.name}</h2>
                    <p className="mt-2 flex items-center gap-1.5 text-xs text-slate-500">
                      <MapPin size={12} className="text-blue-600" /> {p.location}
                    </p>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {(p.metrics || []).slice(0, 3).map((m, j) => (
                        <span key={j} className="px-2.5 py-1 rounded-md bg-blue-50/40 border border-blue-200/50 text-[11px] text-blue-700">
                          <span className="font-semibold">{m.value}</span> · {m.label}
                        </span>
                      ))}
                    </div>
                    <span className="mt-5 inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.15em] text-blue-600/80 group-hover:text-blue-600 transition-colors">
                      Read case study <ArrowUpRight size={13} />
                    </span>
                  </div>
                </Link>
              </FadeUp>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
